/**
 * React Query mutation hooks for manual readings.
 * Handles create, update, and delete operations.
 */

import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { useGuardedMutation } from "model/useGuardedMutation";
import type { MoveEvent } from "model/useAnimalGroupQueries";
import {
  EventType,
  ManualDMInputResponse,
  manualInputApi,
  manualInputApiV1,
  prepareReadingForApi,
  processReadingResponse,
} from "./readingsApiHelpers";

function isGrazingReading(reading: ManualDMInputResponse): boolean {
  const eventType = reading.manualDryMatterInput.event?.eventType;
  return (
    eventType === EventType.GrazingEvent ||
    eventType === EventType.PartialGrazingEvent
  );
}

function animalGroupGuidFromReading(
  reading: ManualDMInputResponse
): string | undefined {
  const guid = reading.manualDryMatterInput.event?.animalGroupGuid;
  return guid || undefined;
}

/** Touched field plus any field with an open grazing event for this group. */
export function fieldGuidsAffectedByGrazingChange(
  queryClient: QueryClient,
  params: { fieldId: string; animalGroupId?: string }
): string[] {
  const guids = new Set<string>([params.fieldId]);
  if (!params.animalGroupId) {
    return [...guids];
  }

  const moveEvents = queryClient.getQueryData<MoveEvent[]>(
    queryKeys.animalGroups.moveEvents(params.animalGroupId)
  );
  for (const ev of moveEvents ?? []) {
    if (ev.dateTimeOut === null && ev.fieldGuid) {
      guids.add(ev.fieldGuid);
    }
  }
  return [...guids];
}

async function invalidateFieldsForGrazingChange(
  queryClient: QueryClient,
  params: { fieldId: string; animalGroupId?: string }
): Promise<void> {
  const fieldGuids = fieldGuidsAffectedByGrazingChange(queryClient, params);
  await Promise.all(
    fieldGuids.map((guid) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.fields.detail(guid),
      })
    )
  );
}

/**
 * Create a reading.
 */
export function useCreateReading() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: async (manualInput: ManualDMInputResponse) => {
      const preparedInput = prepareReadingForApi(manualInput);
      const res = await manualInputApi.createOne(preparedInput);
      const created = await manualInputApi.getOne(res.guid);
      return processReadingResponse(created);
    },
    onSuccess: (data) => {
      const fieldId = data.manualDryMatterInput.field;
      queryClient.setQueryData(
        queryKeys.events.readings.byField(fieldId),
        (old: ManualDMInputResponse[] | undefined) => [...(old || []), data]
      );
    },
  });
}

/**
 * Update a reading.
 */
export function useUpdateReading() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: async (manualInput: ManualDMInputResponse) => {
      const preparedInput = prepareReadingForApi(manualInput);
      await manualInputApiV1.updateOne(preparedInput);
      const updated = await manualInputApi.getOne(manualInput.guid);
      return processReadingResponse(updated);
    },
    onSuccess: async (data) => {
      const fieldId = data.manualDryMatterInput.field;
      queryClient.setQueryData(
        queryKeys.events.readings.byField(fieldId),
        (old: ManualDMInputResponse[] | undefined) =>
          (old || []).map((r) => (r.guid === data.guid ? data : r))
      );
      if (isGrazingReading(data)) {
        await invalidateFieldsForGrazingChange(queryClient, {
          fieldId,
          animalGroupId: animalGroupGuidFromReading(data),
        });
      }
    },
  });
}

/**
 * Delete a reading.
 */
export function useDeleteReading() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: async (params: { readingId: string; fieldId: string }) => {
      await manualInputApiV1.deleteOne(params.readingId);
      return params;
    },
    onSuccess: async ({ readingId, fieldId }) => {
      const readings = queryClient.getQueryData<ManualDMInputResponse[]>(
        queryKeys.events.readings.byField(fieldId)
      );
      const deleted = readings?.find((r) => r.guid === readingId);

      queryClient.setQueryData(
        queryKeys.events.readings.byField(fieldId),
        (old: ManualDMInputResponse[] | undefined) =>
          (old || []).filter((r) => r.guid !== readingId)
      );

      if (deleted && isGrazingReading(deleted)) {
        await invalidateFieldsForGrazingChange(queryClient, {
          fieldId,
          animalGroupId: animalGroupGuidFromReading(deleted),
        });
      }
    },
  });
}

/**
 * Create multiple readings in bulk.
 */
export function useCreateReadingsBulk() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: async (manualInputs: Array<ManualDMInputResponse>) => {
      const preparedInputs = manualInputs.map(prepareReadingForApi);

      const res = await fetch(
        `${import.meta.env.VITE_API_ENDPOINT}/v1/manualDryMatterInput/bulk`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ manualDryMatterInputs: preparedInputs }),
        }
      );
      return res.json() as Promise<unknown>;
    },
    onSuccess: async () => {
      // Invalidate all readings queries to refetch
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.readings.all,
      });
    },
  });
}
